import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { checklistService, datedChecklistService } from '../services/api';
import { ChecklistItem, ChecklistType } from '../types/api';
import { buildCategoryTree, CategoryTreeNode } from '../utils/pasoTags';

interface GlobalPasoTagsModalProps {
  open: boolean;
  onClose: () => void;
}

const checklistTypes: ChecklistType[] = [
  'escalated',
  'wsfj_extremely_urgent',
  'want_to_do',
  'wsfj_more_value_sooner',
  'wsfj_no_rush',
];

const collectCategoryPaths = (items: ChecklistItem[]): string[] => {
  const seen = new Set<string>();
  const categoryPaths: string[] = [];

  items.forEach(item => {
    (item.tags?.categories || []).forEach(path => {
      const normalized = path.trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) {
        return;
      }
      seen.add(key);
      categoryPaths.push(normalized);
    });
  });

  return categoryPaths;
};

const renderTree = (nodes: CategoryTreeNode[], depth = 0): React.ReactNode => (
  <Box>
    {nodes.map(node => (
      <Box key={node.fullPath} sx={{ ml: depth * 2.5, py: 0.25 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            color: 'text.primary',
          }}
        >
          {`- ${node.label}`}
        </Typography>
        {node.children.length > 0 && renderTree(node.children, depth + 1)}
      </Box>
    ))}
  </Box>
);

const GlobalPasoTagsModal: React.FC<GlobalPasoTagsModalProps> = ({ open, onClose }) => {
  const [tagTree, setTagTree] = useState<CategoryTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTags = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [regularResponses, datedMetadataResponse] = await Promise.all([
        Promise.all(checklistTypes.map(type => checklistService.getChecklist(type))),
        datedChecklistService.getAllChecklists(),
      ]);

      if (!datedMetadataResponse.success || !datedMetadataResponse.data) {
        throw new Error(datedMetadataResponse.error || 'Failed to load dated checklists');
      }

      const datedResponses = await Promise.all(
        datedMetadataResponse.data.checklists.map(checklist => datedChecklistService.getChecklist(checklist.date))
      );

      const allItems: ChecklistItem[] = [];

      regularResponses.forEach(response => {
        if (response.success && response.data) {
          allItems.push(...response.data.items);
        }
      });

      datedResponses.forEach(response => {
        if (response.success && response.data) {
          allItems.push(...response.data.items);
        }
      });

      setTagTree(buildCategoryTree(collectCategoryPaths(allItems)));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load tags');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadTags();
  }, [open, loadTags]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>View Tags</DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: 180, pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Non-special paso tags across the application.
          </Typography>

          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading tags...
              </Typography>
            </Box>
          )}

          {!isLoading && error && <Alert severity="error">{error}</Alert>}

          {!isLoading && !error && tagTree.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No tags found.
            </Typography>
          )}

          {!isLoading && !error && tagTree.length > 0 && renderTree(tagTree)}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalPasoTagsModal;